import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Truck, User, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

export const RegisterForm = () => {
  const [searchParams] = useSearchParams();
  const defaultType = searchParams.get('type') || 'customer';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    user_type: defaultType
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('As palavras-passe não coincidem');
      return;
    }

    if (formData.password.length < 6) {
      setError('A palavra-passe deve ter pelo menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      const user = await register({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        user_type: formData.user_type
      });
      if (user.user_type === 'driver') {
        navigate('/driver');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao criar conta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-600 hover:text-tuggo-mint mb-8">
          <ArrowLeft className="w-4 h-4" />
          Voltar para o início
        </Link>

        <div className="bg-white rounded-3xl p-8 shadow-xl">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-tuggo-mint rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Truck className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Criar Conta</h1>
            <p className="text-gray-600">Junte-se à TugGo hoje</p>
          </div>

          {/* User Type Toggle */}
          <div className="flex bg-gray-100 rounded-full p-1 mb-6">
            <button
              type="button"
              className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-all ${
                formData.user_type === 'customer'
                  ? 'bg-tuggo-mint text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              onClick={() => setFormData({ ...formData, user_type: 'customer' })}
              data-testid="register-customer-tab"
            >
              <User className="w-4 h-4 inline mr-2" />
              Cliente
            </button>
            <button
              type="button"
              className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-all ${
                formData.user_type === 'driver'
                  ? 'bg-tuggo-mint text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              onClick={() => setFormData({ ...formData, user_type: 'driver' })}
              data-testid="register-driver-tab"
            >
              <Truck className="w-4 h-4 inline mr-2" />
              Estafeta
            </button>
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl mb-6" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Nome completo</Label>
              <Input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="O seu nome"
                required
                className="mt-2"
                data-testid="register-name-input"
              />
            </div>

            <div>
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="o.seu@email.com"
                required
                className="mt-2"
                data-testid="register-email-input"
              />
            </div>

            <div>
              <Label htmlFor="phone">Telemóvel</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+351 912 345 678"
                required
                className="mt-2"
                data-testid="register-phone-input"
              />
            </div>

            <div>
              <Label htmlFor="password">Palavra-passe</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Mínimo 6 caracteres"
                  required
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword">Confirmar Palavra-passe</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Repita a palavra-passe"
                required
                className="mt-2"
                data-testid="register-confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-tuggo-mint text-white hover:bg-tuggo-mint/90 rounded-full py-6 mt-6"
              data-testid="register-submit-btn"
            >
              {loading ? 'A criar conta...' : `Criar conta como ${formData.user_type === 'driver' ? 'Estafeta' : 'Cliente'}`}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <span className="text-gray-600">Já tem uma conta? </span>
            <Link to="/login" className="text-tuggo-mint font-semibold hover:underline">
              Entrar
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
